import * as THREE from "three";
import { Utils } from "../../../utils/utils";
import AppData from "../../../app.data";
import Renderer from "../../../renderer/renderer";
import { Loader } from "../../assets/loader";
import { applyRuntimeCameraHeight, getMoodCameraHeight } from "../../../../modals/render.settings.camera";

const appData = new AppData();

export class SpaceEnvironmentManager {
    mood: any = null;
    lightsHolder: THREE.Object3D;
    targetsHolder: THREE.Object3D;
    lightHelper: THREE.Object3D;
    private lastHdriUrl: string | null = null;
    private lastSkyUrl: string | null = null;
    private lastSkyModelName: string | null = null;
    private lastLightMapUrl: string | null = null;
    private lightMapTexture: THREE.Texture | null = null;
    private lightMapMeshNames: Set<string> = new Set<string>();
    private originalSkyMaterials: Map<string, THREE.Material | THREE.Material[]> = new Map<string, THREE.Material | THREE.Material[]>();

    constructor(
        private renderer: Renderer,
        private loader: Loader,
        private utils: Utils
    ) { }

    private hasSpaceScene(): boolean {
        return Boolean(appData.spaceScene);
    }

    private cloneMaterialDefinition(material: THREE.Material | THREE.Material[]) {
        if (Array.isArray(material)) {
            return material.map((item) => item.clone());
        }
        return material.clone();
    }

    private disposeMaterial(material: THREE.Material | THREE.Material[] | undefined) {
        if (!material) return;
        if (Array.isArray(material)) {
            material.forEach((item) => item.dispose?.());
            return;
        }
        material.dispose?.();
    }

    private cacheOriginalSkyMaterial(mesh: THREE.Mesh) {
        if (this.originalSkyMaterials.has(mesh.uuid)) return;
        this.originalSkyMaterials.set(mesh.uuid, this.cloneMaterialDefinition(mesh.material as THREE.Material | THREE.Material[]));
    }

    private clearHDR() {
        if (!this.hasSpaceScene()) return;
        const spaceEnvironment = appData.spaceScene.environment as any;
        if (spaceEnvironment && typeof spaceEnvironment.dispose === "function") {
            spaceEnvironment.dispose();
        }
        appData.spaceScene.environment = null;
        if (appData.artworksScene) {
            appData.artworksScene.environment = null;
        }
        this.lastHdriUrl = null;
    }

    private clearSkyImage(skyModelName: string) {
        if (!this.hasSpaceScene()) return;
        const candidateNames = [skyModelName, this.lastSkyModelName, "sky"].filter((name): name is string => Boolean(name));
        for (const candidate of candidateNames) {
            const skyMesh = appData.spaceScene.getObjectByName(candidate);
            if (!(skyMesh instanceof THREE.Mesh)) continue;
            const material = skyMesh.material as THREE.Material | THREE.Material[] | undefined;
            const cachedOriginalMaterial = this.originalSkyMaterials.get(skyMesh.uuid);
            if (cachedOriginalMaterial) {
                this.disposeMaterial(material);
                skyMesh.material = this.cloneMaterialDefinition(cachedOriginalMaterial) as THREE.Material | THREE.Material[];
                if (Array.isArray(skyMesh.material)) {
                    skyMesh.material.forEach((item) => {
                        item.needsUpdate = true;
                    });
                } else {
                    skyMesh.material.needsUpdate = true;
                }
                continue;
            }

            const legacyMaterial = material as any;
            if (legacyMaterial?.map) {
                legacyMaterial.map.dispose?.();
                legacyMaterial.map = null;
                legacyMaterial.needsUpdate = true;
            }
        }
        this.lastSkyUrl = null;
        this.lastSkyModelName = null;
    }

    public initializeHolders() {
        if (!this.hasSpaceScene()) return;
        // create 3 dummies objects one for lights, one for targets and one for light helpers
        this.lightsHolder = new THREE.Object3D();
        this.lightsHolder.name = "lightsHolder";
        this.targetsHolder = new THREE.Object3D();
        this.targetsHolder.name = "targetsHolder";
        this.lightHelper = new THREE.Object3D();
        this.lightHelper.name = "lightHelper";

        appData.spaceScene.add(this.lightsHolder);
        appData.spaceScene.add(this.targetsHolder);
        appData.spaceScene.add(this.lightHelper);
    }

    public async changeMood(mood: any, local?: any) {
        try {
            if (!this.hasSpaceScene()) return;
            this.mood = appData.spaceData.data.moods[mood];
            if (local) this.mood = local;
            applyRuntimeCameraHeight(appData, getMoodCameraHeight(this.mood));

            // Update renderer settings
            // this.renderer.killRenderer();
            // this.renderer.initRenderer(local);
            // this.renderer.startAnimationLoop();

            this.setFog();

            // Update lights
            this.utils.removeLights(appData);
            this.utils.createLights(this.mood.lights, appData);

            // Update sky
            const hdriUrl = this.mood?.sky?.hdriUrl || "";
            const skyUrl = this.mood?.sky?.skyImageUrl || "";
            const skyModelName = this.mood?.sky?.skyModelName || "sky";
            const lightMapUrl = this.mood?.lightMap?.lightMapUrl || "";
            const shouldReloadHdri = Boolean(hdriUrl) && hdriUrl !== this.lastHdriUrl;
            const shouldReloadSky = Boolean(skyUrl) && (skyUrl !== this.lastSkyUrl || skyModelName !== this.lastSkyModelName);
            const shouldReloadLightMap = Boolean(lightMapUrl) && lightMapUrl !== this.lastLightMapUrl;

            if (shouldReloadHdri) {
                try {
                    const hdri = await this.loader.loadHDR(hdriUrl, this.renderer.renderer);
                    if (hdri) {
                        this.handleHDR(this.mood, hdri);
                    }
                } catch (error) {
                    console.warn("SpaceEnvironmentManager: Failed to load mood HDR", { hdriUrl, error });
                }
            } else if (hdriUrl) {
                this.updateHdrIntensity(this.mood);
            } else {
                this.clearHDR();
            }

            if (shouldReloadSky) {
                try {
                    const sky = await this.loader.loadTexture(skyUrl);
                    if (sky) {
                        this.handleSkyImage(this.mood, sky);
                    }
                } catch (error) {
                    console.warn("SpaceEnvironmentManager: Failed to load mood sky texture", { skyUrl, error });
                }
            } else if (!skyUrl) {
                this.clearSkyImage(skyModelName);
            }

            if (shouldReloadLightMap) {
                this.applyLightMapToSelectedMeshes(this.mood, null);
                this.clearLightMapTexture();
                try {
                    const lightMap = await this.loader.loadTexture(lightMapUrl);
                    this.handleLightMap(this.mood, lightMap);
                } catch (error) {
                    console.warn("SpaceEnvironmentManager: Failed to load mood light map texture", { lightMapUrl, error });
                    this.clearLightMapTexture();
                    this.applyLightMapToSelectedMeshes(this.mood, null);
                }
            } else if (lightMapUrl) {
                this.applyLightMapToSelectedMeshes(this.mood, this.lightMapTexture);
            } else {
                this.clearLightMapTexture();
                this.applyLightMapToSelectedMeshes(this.mood, null);
            }

            this.renderer.syncWithMood(this.mood);

            // Queue a render
            if (this.renderer) {
                this.renderer.addToRenderQueue();
            }
        } catch (error) {
            console.error("SpaceEnvironmentManager: Error in changeMood", error);
            throw error;
        }
    }

    public handleHDR(mood: any, hdri: any) {
        if (!this.hasSpaceScene()) return;
        const previousEnvironment = appData.spaceScene.environment as any;
        if (previousEnvironment && previousEnvironment !== hdri && typeof previousEnvironment.dispose === "function") {
            previousEnvironment.dispose();
        }
        appData.spaceScene.environment = hdri;
        appData.artworksScene.environment = hdri;
        this.lastHdriUrl = mood?.sky?.hdriUrl || null;
        this.updateHdrIntensity(mood);
    }

    public handleSkyImage(mood: any, image: any) {
        if (!this.hasSpaceScene()) return;
        if (!image) return;
        const skyMeshName = mood.sky.skyModelName;
        const skyMesh = appData.spaceScene.getObjectByName(skyMeshName);
        image.flipY = false;
        image.repeat.set(1, 1);
        image.wrapS = THREE.RepeatWrapping;
        image.wrapT = THREE.RepeatWrapping;
        if (skyMesh instanceof THREE.Mesh) {
            this.cacheOriginalSkyMaterial(skyMesh);
            this.disposeMaterial(skyMesh.material as THREE.Material | THREE.Material[]);
            skyMesh.material = new THREE.MeshBasicMaterial({ map: image, fog: false });
            skyMesh.material.needsUpdate = true;
        }
        this.lastSkyUrl = mood?.sky?.skyImageUrl || null;
        this.lastSkyModelName = skyMeshName || null;
    }

    public handleLightMap(mood: any, lightMap: any) {
        if (!lightMap) {
            this.clearLightMapTexture();
            this.applyLightMapToSelectedMeshes(mood, null);
            return;
        }

        this.clearLightMapTexture();
        this.configureLightMapTexture(lightMap);
        this.lightMapTexture = lightMap;
        this.lastLightMapUrl = mood?.lightMap?.lightMapUrl || null;
        this.applyLightMapToSelectedMeshes(mood, this.lightMapTexture);
    }

    private configureLightMapTexture(texture: THREE.Texture) {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.flipY = false;
        (texture as any).channel = 1;
        texture.needsUpdate = true;
    }

    private clearLightMapTexture() {
        if (this.lightMapTexture) {
            this.lightMapTexture.dispose();
            this.lightMapTexture = null;
        }
        this.lastLightMapUrl = null;
    }

    private applyLightMapToMaterial(material: THREE.Material, texture: THREE.Texture | null) {
        const asAny = material as any;
        asAny.lightMap = texture;
        if (texture && typeof asAny.lightMapIntensity !== "number") {
            asAny.lightMapIntensity = 1;
        }
        material.needsUpdate = true;
    }

    private ensureUv2(mesh: THREE.Mesh) {
        const geometry = mesh.geometry as THREE.BufferGeometry;
        if (!geometry) return;
        const uv = geometry.getAttribute("uv");
        const uv2 = geometry.getAttribute("uv2");
        if (uv && !uv2) {
            geometry.setAttribute("uv2", uv.clone());
        }
    }

    private applyLightMapToMesh(mesh: THREE.Mesh, texture: THREE.Texture | null) {
        this.ensureUv2(mesh);
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach((material) => this.applyLightMapToMaterial(material, texture));
            return;
        }
        if (mesh.material) {
            this.applyLightMapToMaterial(mesh.material, texture);
        }
    }

    private forEachMeshByName(meshName: string, callback: (mesh: THREE.Mesh) => void) {
        if (!this.hasSpaceScene()) return;
        appData.spaceScene.traverse((child) => {
            if (child instanceof THREE.Mesh && child.name === meshName) {
                callback(child);
            }
        });
    }

    private applyLightMapToSelectedMeshes(mood: any, texture: THREE.Texture | null) {
        const selected = Array.isArray(mood?.lightMap?.selectedMeshes) ? mood.lightMap.selectedMeshes : [];
        const selectedMeshNames = new Set<string>(selected.filter((name: any) => typeof name === "string" && name.length > 0));

        const touchedMeshNames = new Set<string>([
            ...Array.from(this.lightMapMeshNames),
            ...Array.from(selectedMeshNames)
        ]);

        touchedMeshNames.forEach((meshName) => {
            const shouldApply = selectedMeshNames.has(meshName) && Boolean(texture);
            this.forEachMeshByName(meshName, (mesh) => {
                this.applyLightMapToMesh(mesh, shouldApply ? texture : null);
            });
        });

        this.lightMapMeshNames = selectedMeshNames;
    }

    private updateHdrIntensity(mood: any) {
        if (!this.hasSpaceScene()) return;
        const hdrIntensity = mood?.sky?.hdrIntensity ?? 1;
        const spaceSceneAny = appData.spaceScene as any;
        const artworksSceneAny = appData.artworksScene as any;
        if (spaceSceneAny && typeof spaceSceneAny.environmentIntensity === "number") {
            spaceSceneAny.environmentIntensity = hdrIntensity;
        }
        if (artworksSceneAny && typeof artworksSceneAny.environmentIntensity === "number") {
            artworksSceneAny.environmentIntensity = hdrIntensity;
        }
        const applyToMaterial = (material: THREE.Material) => {
            if (!material) return;
            const asAny = material as any;
            if (typeof asAny.envMapIntensity === "number") {
                asAny.envMapIntensity = hdrIntensity;
                material.needsUpdate = true;
            }
        };

        const applyToMesh = (mesh: THREE.Mesh) => {
            if (!mesh.material) return;
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach((material) => applyToMaterial(material));
                return;
            }
            applyToMaterial(mesh.material);
        };

        appData.spaceScene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                applyToMesh(child);
            }
        });
        appData.artworksScene?.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                applyToMesh(child);
            }
        });
    }

    private updateArtworkFogSettings(enabled: boolean) {
        appData.artworksScene?.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) return;
            if (Array.isArray(child.material)) {
                child.material.forEach((material: THREE.Material) => {
                    (material as any).fog = enabled;
                    material.needsUpdate = true;
                });
                return;
            }
            if (child.material) {
                (child.material as any).fog = enabled;
                child.material.needsUpdate = true;
            }
        });
    }

    public setFog() {
        if (!this.mood || !this.hasSpaceScene()) return;

        const fogEnabled = this.mood?.fog?.enabled ?? true;
        const shouldApplySceneFog = fogEnabled && !(appData as any).debugCameraEnabled;
        if (shouldApplySceneFog) {
            const color = new THREE.Color(this.mood.fog.color);
            const near = this.mood.fog.near;
            const far = this.mood.fog.far;
            appData.spaceScene.fog = new THREE.Fog(color, near, far);
        } else {
            appData.spaceScene.fog = null;
        }

        const applyFogToArtworks = shouldApplySceneFog && Boolean(this.mood?.fog?.applyFogToArtworks);
        this.updateArtworkFogSettings(applyFogToArtworks);
    }

    public dispose() {
        if (this.hasSpaceScene()) {
            this.applyLightMapToSelectedMeshes(this.mood, null);
            this.clearLightMapTexture();
            if (appData.spaceScene.background) {
                appData.spaceScene.background = null;
            }
            if (appData.spaceScene.environment) {
                appData.spaceScene.environment.dispose();
                appData.spaceScene.environment = null;
            }
        }
        this.lightsHolder = null;
        this.targetsHolder = null;
        this.lightHelper = null;
        this.mood = null;
        this.lastHdriUrl = null;
        this.lastSkyUrl = null;
        this.lastSkyModelName = null;
        this.lightMapMeshNames.clear();
        this.originalSkyMaterials.clear();
    }
}
